/**
 * Connection-state derivation for the Xero workspace. One place computes the
 * pill state, the disconnected-fallback variant, and the token-expiry window so
 * the header, KPI strip, sidebar gate and fallback card never disagree.
 */
import type { RouterOutputs } from '@/lib/trpc';

export type XeroStatus = RouterOutputs['xero']['getStatus'];

/** Days before token expiry below which we surface a warning chip. */
export const TOKEN_EXPIRY_WARNING_DAYS = 7;

export type ConnectionPill = 'connected' | 'expiring' | 'not_connected' | 'broken';

/** Which centred fallback to render when the tabs can't show. */
export type DisconnectedVariant = 'not_configured' | 'not_connected' | 'token_expired';

export const daysUntil = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return null;
  return (ts - Date.now()) / 86_400_000;
};

export const isTokenExpired = (status: XeroStatus | undefined): boolean => {
  const days = daysUntil(status?.expiresAt);
  return days !== null && days <= 0;
};

/** True when the workspace tabs can render (connected and token still valid). */
export const isWorkspaceReady = (status: XeroStatus | undefined): boolean =>
  Boolean(status?.connected) && !isTokenExpired(status);

export const connectionPill = (status: XeroStatus | undefined): ConnectionPill => {
  // An expired token reads red ('broken') whether or not the backend has
  // already zeroed `connected` on expiry detection; a never-connected org
  // reads slate ('not_connected').
  if (isTokenExpired(status)) return 'broken';
  if (!status?.connected) return 'not_connected';
  const days = daysUntil(status.expiresAt);
  if (days !== null && days < TOKEN_EXPIRY_WARNING_DAYS) return 'expiring';
  return 'connected';
};

export const disconnectedVariant = (status: XeroStatus | undefined): DisconnectedVariant => {
  if (!status?.configured) return 'not_configured';
  // Identify an expired token regardless of whether the backend keeps
  // `connected` true or flips it false on expiry — the user needs the
  // "reconnect, auto-push paused" copy either way.
  if (isTokenExpired(status)) return 'token_expired';
  return 'not_connected';
};
